import { useDispatch } from 'react-redux'
import { useTheme } from '@mui/material'
import noop from 'lodash/noop'
import { get } from 'utils/lodash'
import {
  setAddToGroupDialogState,
  setInvoicesDeleteConfirmation,
  setInvoicesDeleteUnpaidConfirmation,
  setInvoicesRevertToDraftConfirmation,
  setInvoicesRevertCreditMemoToDraftConfirmation,
  setInvoicesMoveToAccountCreditConfirmation,
  setInvoicesSideView,
  setInvoicesSelectedInvoice,
  setInvoicesSendPostData,
  setCustomEmailDialogState,
} from 'slices/billing/invoicesSlice'
import { useInvoiceActionsCore } from './useInvoiceActionsCore'

/**
 * Unified hook for invoice actions across menu and data grid contexts
 *
 * @param {Object} selectedInvoice - The invoice object to perform actions on
 * @param {Object} options - Configuration options
 * @param {Function} [options.handleRefetch=noop] - Callback to refresh data after actions
 * @param {Function} [options.handleRegenerateRefetch=handleRefetch] - Callback after regeneration actions;
 *   defaults to handleRefetch. Override when regeneration needs distinct side effects (e.g. start polling).
 * @param {string} [options.context='menu'] - UI context ('menu' or 'datagrid')
 * @param {Function} [options.onClose=noop] - Callback to close the UI component (menu only)
 *
 * @returns {Object} { availableActions, handleAction, disabledProps, loadingStates }
 */

export const useInvoiceActions = (selectedInvoice = {}, options = {}) => {
  const {
    handleRefetch = noop,
    handleRegenerateRefetch = handleRefetch,
    context = 'menu', // 'menu' or 'datagrid'
    onClose = noop, // Only used for menu context to close the menu itself
  } = options

  const dispatch = useDispatch()
  const theme = useTheme()

  // Context-specific field mappers
  /**
   * The field names for invoice ID and unposted amount differ between 'datagrid' and 'menu' contexts:
   * - In 'datagrid' context, invoices come from the data grid and use 'invoiceId' and 'unPostedAmountCents'.
   * - In 'menu' context, invoices use 'id' and 'totalCents'.
   * This mapping is critical for correct operation. If you change the data source or context, ensure the field names match.
   */
  const getInvoiceId = invoice => (context === 'datagrid' ? get(invoice, 'invoiceId') : get(invoice, 'id'))
  const getUnPostedAmountCents = invoice => (context === 'datagrid' ? get(invoice, 'unPostedAmountCents') : get(invoice, 'totalCents'))

  const getEditInvoiceData = invoice => {
    const unPostedAmountCents = getUnPostedAmountCents(invoice)

    return {
      ...invoice,
      invoiceId: getInvoiceId(invoice),
      unPostedAmountCents,
      isCurrentUnPosted: unPostedAmountCents > 0,
    }
  }

  const coreOptions = {
    showAddToGroupAction: true,
    handleRefetch,
    handleRegenerateRefetch,
    getInvoiceId,
    getInvoiceNumber: invoice => get(invoice, 'invoiceNumber'),
  }

  const { availableActions, coreActions, loadingStates } = useInvoiceActionsCore(selectedInvoice, coreOptions)

  const invoiceId = getInvoiceId(selectedInvoice)
  const invoiceNumber = get(selectedInvoice, 'invoiceNumber')

  // Action handlers that manage state and delegate to core actions
  const actionHandlers = {
    edit: () => {
      const invoiceData = getEditInvoiceData(selectedInvoice)

      dispatch(setInvoicesSelectedInvoice(invoiceData))
      dispatch(
        setInvoicesSideView({
          isEditInvoiceWOListingOpen: true,
          isOpen: false,
        })
      )
      onClose()
    },

    makePayment: () => {
      coreActions.makePayment()
      onClose()
    },
    post: () => {
      coreActions.post()
      onClose()
    },

    postAndSend: () => {
      coreActions.postAndSend()
      onClose()
    },

    regenerate: () => {
      coreActions.regenerate()
      onClose()
    },

    confirmRegenerate: () => {
      coreActions.confirmRegenerate()
    },

    regenerateAndRepost: () => {
      coreActions.regenerateAndRepost()
      onClose()
    },

    email: async () => {
      try {
        await coreActions.email()
      } catch (error) {
        if (error.message === 'Invoice PDF not available') {
          dispatch(
            setInvoicesSendPostData({
              isOpenInvoiceSendFailureConfirmation: true,
              draftInvoicesWithoutPDF: [invoiceNumber],
            })
          )
        }
      }
      onClose()
    },
    customEmail: () => {
      const id = getInvoiceId(selectedInvoice)
      const totalCents = context === 'datagrid' ? get(selectedInvoice, 'amountCents', 0) : get(selectedInvoice, 'totalCents', 0)

      const normalizedInvoice = {
        ...selectedInvoice,
        id,
        totalCents,
      }

      dispatch(
        setCustomEmailDialogState({
          isOpen: true,
          invoice: normalizedInvoice,
        })
      )
      onClose()
    },
    addToGroup: () => {
      dispatch(
        setAddToGroupDialogState({
          isOpen: true,
          invoiceIds: [invoiceId],
        })
      )
      onClose()
    },

    moveToAccountCredit: () => {
      const accountId = get(selectedInvoice, 'accountId')
      const paymentNumbers = get(selectedInvoice, 'payments', []).map(({ paymentNumber }) => paymentNumber)

      dispatch(
        setInvoicesMoveToAccountCreditConfirmation({
          isOpen: true,
          accountId,
          invoiceNumbers: [invoiceNumber],
          paymentNumbers,
        })
      )
      onClose()
    },

    revertToDraft: () => {
      dispatch(
        setInvoicesRevertToDraftConfirmation({
          isOpen: true,
          invoiceId,
        })
      )
      onClose()
    },

    confirmRevertToDraft: () => {
      coreActions.confirmRevertToDraft()
    },

    revertCreditMemoToDraft: () => {
      dispatch(
        setInvoicesRevertCreditMemoToDraftConfirmation({
          isOpen: true,
          invoiceId,
        })
      )
      onClose()
    },

    confirmRevertCreditMemoToDraft: () => {
      coreActions.confirmRevertCreditMemoToDraft()
    },

    delete: () => {
      dispatch(
        setInvoicesDeleteConfirmation({
          isOpen: true,
          invoiceIds: [invoiceId],
          invoiceNumbers: [invoiceNumber],
        })
      )
      onClose()
    },
    confirmDelete: () => {
      coreActions.confirmDelete()
    },
    deleteUnpaid: () => {
      dispatch(
        setInvoicesDeleteUnpaidConfirmation({
          isOpen: true,
          invoiceId,
        })
      )
      onClose()
    },
    confirmDeleteUnpaid: () => {
      coreActions.confirmDeleteUnpaid()
    },

    removeFromGroup: () => {
      coreActions.removeFromGroup()
      onClose()
    },
  }

  // Main action dispatcher
  const handleAction = actionKey => {
    const handler = actionHandlers[actionKey]
    if (handler) {
      handler()
    }
  }

  const disabledProps = { sx: { opacity: theme.palette.action.disabledOpacity } }

  return {
    availableActions,
    handleAction,
    disabledProps,
    loadingStates,
  }
}

/**
 * Data grid alias for useInvoiceActions with context pre-configured to 'datagrid'
 */
export const useInvoiceActionsDataGrid = (selectedInvoice = {}, options = {}) => {
  return useInvoiceActions(selectedInvoice, { ...options, context: 'datagrid' })
}
